import { useCallback } from 'react';
import { useCustomerMutations } from '../queries/customers.query';
import { useUsageMutations } from '@/shared/queries';
import type { Customer, CustomerDetail } from '../models/customer.model';

// Manager hook for single-customer operations (details + update)
export const useCustomerManager = (id: string) => {
  const { useGetCustomerByIdQuery, updateCustomer } = useCustomerMutations();

  const {
    useGetCustomerDailyUsageQuery,
    useGetCustomerMonthlyUsageQuery,
    useGetCustomerYearlyUsageQuery,
  } = useUsageMutations();

  const {
    data: customer,
    isLoading,
    error,
    isFetching,
  } = useGetCustomerByIdQuery(id);

  const update = useCallback(
    async (data: Partial<CustomerDetail | Customer>, overrideId?: string) => {
      const targetId = overrideId ?? id;
      if (!targetId) {
        throw new Error('Missing customer id for update');
      }
      return updateCustomer.mutateAsync({ id: targetId, data });
    },
    [id, updateCustomer]
  );

  return {
    customer,
    loading: isLoading,
    fetching: isFetching,
    error,
    update,
    updateMutation: updateCustomer,
    useGetCustomerDailyUsageQuery,
    useGetCustomerMonthlyUsageQuery,
    useGetCustomerYearlyUsageQuery,
  };
};
